import React, { useState, useEffect } from "react";
import { assetsPath } from "../utils/assetsPath";

const UploadFile = ({
  id,
  label,
  placeholder,
  register,
  errors,
  watch,
  disabled,
  className,
  emailclass,
  tooltipText,
  value = "",
  classTick,
  maxLength,
  trigger,
  onChange = () => {},
  onInput = () => {},
  liveValidate = true,
  onFilesUploaded = () => {}, // New prop for handling uploaded files
  ...props
}) => {
  const [files, setFiles] = useState([]); // Track multiple files
  const [isUploading, setIsUploading] = useState(false); // Overall upload state
  const [uploadError, setUploadError] = useState(null); // Upload error state
  const [isDragOver, setIsDragOver] = useState(false); // Drag-over state
  const maxFiles = 5; // Set the maximum number of files
  const maxFileSize = 10 * 1024 * 1024; // 10MB in bytes

  useEffect(() => {
    if (watch && liveValidate) {
      const trig = async () => await trigger(id);
      trig();
    }
  }, [watch]);

  const handleFileChange = async (e) => {
    const selectedFiles = e.target.files || e.dataTransfer.files;
    if (selectedFiles) {
      // If the number of files already uploaded plus the new files exceeds the maxFiles limit
      if (files.length + selectedFiles.length > maxFiles) {
        setUploadError(`You can only upload up to ${maxFiles} files.`);
        return;
      }

      setUploadError(null); // Clear any existing error
      const newFiles = Array.from(selectedFiles).map((file) => ({
        file,
        progress: 100, // Files are "uploaded" immediately since we're not uploading to server
        isUploading: false,
      }));
      const updatedFiles = [...files, ...newFiles];
      setFiles(updatedFiles);

      // Notify parent component of all uploaded files
      onFilesUploaded(updatedFiles.map((f) => f.file));
    }
  };

  const cancelUpload = (fileName) => {
    setFiles((prevFiles) => {
      const updatedFiles = prevFiles.filter(
        (file) => file.file.name !== fileName
      );
      onFilesUploaded(updatedFiles.map((f) => f.file));
      return updatedFiles;
    });
  };

  const clearFile = (fileName) => {
    setFiles((prevFiles) => {
      const updatedFiles = prevFiles.filter(
        (file) => file.file.name !== fileName
      );
      onFilesUploaded(updatedFiles.map((f) => f.file));
      return updatedFiles;
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    handleFileChange(e);
  };

  return (
    <div className={`tw-relative tw-w-full ${className}`}>
      {label && (
        <label className="tw-mb-2.5 tw-block tw-text-base tw-font-bold tw-text-dis-navy" htmlFor={id}>
          {label}
        </label>
      )}

      {/* Drag and Drop Area */}

      {files.length < maxFiles && (
        <>
          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragLeave={handleDragLeave}
            className="tw-relative tw-mb-[15px] tw-mt-2.5 tw-flex tw-min-h-[180px] tw-w-full tw-cursor-pointer tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-rounded-xl tw-border-2 tw-border-dashed tw-border-[#dee2e6] tw-bg-[#f8f9fa] tw-py-5 tw-text-center tw-text-sm tw-font-medium tw-transition-all hover:tw-border-[#1e54b7] hover:tw-bg-[#f0f4ff]"
          >
            {isDragOver && (
              <div className="tw-pointer-events-none tw-absolute tw-left-0 tw-top-0 tw-z-10 tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-rounded-xl tw-border-2 tw-border-dashed tw-border-[#1e54b7] tw-bg-[#e3f2fd] tw-text-center tw-text-base tw-font-semibold tw-text-[#1e54b7]">Drop files here to upload…</div>
            )}
            <div className="tw-pointer-events-none tw-flex tw-flex-col tw-items-center">
              <img
                src={assetsPath.cloudUpload}
                alt="Upload"
                className="tw-mb-3 tw-h-[35px] tw-w-[35px] tw-opacity-70"
              />
              <div className="tw-mb-[5px] tw-text-sm tw-font-semibold tw-text-[#495057]">
                Choose image or drag and drop it here
              </div>
              <div className="tw-text-xs tw-font-normal tw-text-[#6c757d]">JPG, PNG up to 10MB</div>
            </div>
            <input
              type="file"
              id="uploadFiles"
              onChange={handleFileChange}
              disabled={disabled || files.length >= maxFiles}
              className="tw-sr-only"
              hidden
              multiple // Allow multiple files to be selected
            />
            <label htmlFor="uploadFiles" className="tw-mt-2 tw-cursor-pointer tw-rounded-lg tw-bg-[#1e54b7] tw-px-5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-white tw-transition-colors hover:tw-bg-[#1e5bc6]">
              Browse Files
            </label>
          </div>
          {uploadError && <span className="tw-mt-[5px] tw-mb-[5px] tw-block tw-text-sm tw-text-dis-error">{uploadError}</span>}
        </>
      )}
      {/* Uploaded files list */}
      <div className="tw-mt-[15px] tw-flex tw-flex-col tw-gap-3">
        {files.map((fileObj) => (
          <div key={fileObj.file.name} className="tw-relative tw-flex tw-items-center tw-rounded-lg tw-border tw-border-[#e9ecef] tw-bg-white tw-p-3 tw-shadow-[0_1px_3px_rgba(0,0,0,0.1)] tw-transition-shadow hover:tw-shadow-[0_2px_8px_rgba(0,0,0,0.15)]">
            {/* Thumbnail */}
            <div className="tw-mr-3 tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-md tw-border tw-border-[#dee2e6] tw-bg-[#f8f9fa]">
              {fileObj.file.type.startsWith("image/") ? (
                <img
                  src={URL.createObjectURL(fileObj.file)}
                  alt={fileObj.file.name}
                  className="tw-h-full tw-w-full tw-object-cover"
                />
              ) : (
                <div className="tw-text-2xl">📄</div>
              )}
            </div>

            {/* File info */}
            <div className="tw-min-w-0 tw-flex-1">
              <div className="tw-mb-0.5 tw-overflow-hidden tw-whitespace-nowrap tw-text-ellipsis tw-text-sm tw-font-medium tw-text-[#495057]">{fileObj.file.name}</div>
              <div className="tw-text-xs tw-font-normal tw-text-[#6c757d]">
                {(fileObj.file.size / (1024 * 1024)).toFixed(2)} MB
              </div>
            </div>

            {/* Delete button */}
            <button
              className="tw-ml-3 tw-flex tw-h-8 tw-w-8 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-md tw-border-0 tw-bg-[#dc3545] tw-text-base tw-font-bold tw-text-white tw-transition-transform hover:tw-scale-105 hover:tw-bg-[#c82333]"
              onClick={() => clearFile(fileObj.file.name)}
              title="Remove file"
            >
              ✕
            </button>

            {/* Progress bar for uploading files */}
            {fileObj.isUploading && (
              <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-rounded-b-lg tw-bg-white/90 tw-px-3 tw-py-2">
                <div className="tw-relative tw-mb-1 tw-h-1 tw-w-full tw-max-w-[290px] tw-rounded-sm tw-bg-[#e9ecef]">
                  <div
                    className="tw-absolute tw-left-0 tw-top-0 tw-h-full tw-rounded-sm tw-bg-[#1e54b7] tw-transition-all"
                    style={{ width: `${fileObj.progress}%` }}
                  />
                </div>
                <div className="tw-text-center tw-text-xs tw-font-medium tw-text-[#495057]">{`${Math.round(
                  fileObj.progress
                )}% complete`}</div>
              </div>
            )}
          </div>
        ))}
      </div>
      {files.length >= maxFiles && (
        <div className="tw-mt-2.5 tw-mb-2.5 tw-block tw-text-sm tw-text-dis-error">
          Max file limit reached.
        </div>
      )}
    </div>
  );
};

export default UploadFile;
